import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Package, ShoppingCart, Trash2, Star } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import RecoRail from "@/components/RecoRail";
import { toast } from "sonner";

export default function WishlistPage() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => api.get("/marketplace/wishlist").then((r) => setItems(r.data.items || r.data.products || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/marketplace/wishlist/${id}`); toast.success("Removed from wishlist"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const addCart = async (id) => {
    try { await api.post("/marketplace/cart/add", { product_id: id, qty: 1 }); window.dispatchEvent(new Event("cart-updated")); toast.success("Added to cart"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add to cart"); }
  };
  const disc = (p) => (p.mrp && p.price && p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0);

  return (
    <div data-testid="wishlist-page">
      <PageHeader title="My Wishlist" icon={Heart} subtitle="Products you've saved. Add them to your cart when you're ready." />
      {items && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="wishlist-empty">
          <Heart className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">Your wishlist is empty</p>
          <button onClick={() => nav("/app/shop")} className="mt-3 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">Start shopping with Suffle</button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="wishlist-grid">
        {(items || []).map((p) => (
          <div key={p.product_id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`wish-${p.product_id}`}>
            <div onClick={() => nav(`/product/${p.slug || p.product_id}`)} className="aspect-square cursor-pointer bg-slate-100">
              {p.image || (p.images || [])[0] ? <img src={p.image || p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}
            </div>
            <div className="p-3">
              <p onClick={() => nav(`/product/${p.slug || p.product_id}`)} className="line-clamp-2 cursor-pointer text-sm font-medium">{p.name}</p>
              <div className="mt-1 flex items-baseline gap-1"><span className="font-bold">₹{Math.round(p.price || 0)}</span>{disc(p) > 0 && <><span className="text-xs text-slate-400 line-through">₹{Math.round(p.mrp)}</span><span className="text-xs font-semibold text-emerald-600">{disc(p)}%</span></>}</div>
              {(p.rating_avg || p.rating) ? <p className="mt-1 flex items-center gap-1 text-xs text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {p.rating_avg || p.rating}</p> : null}
              {p.in_stock === false && <p className="mt-1 text-xs font-medium text-rose-500">Currently unavailable</p>}
              <div className="mt-2 flex gap-1.5">
                <button onClick={() => addCart(p.product_id)} disabled={p.in_stock === false} data-testid={`wish-cart-${p.product_id}`} className="inline-flex flex-1 items-center justify-center gap-1 rounded-lg brand-gradient-bg px-2 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><ShoppingCart className="h-3.5 w-3.5" /> Add</button>
                <button onClick={() => remove(p.product_id)} data-testid={`wish-remove-${p.product_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="mt-8">
          <RecoRail path="/marketplace/recommendations/complete-the-look" rail="complete-the-look" title="Complete the look" subtitle="Real products that pair well with what you've saved" />
        </div>
      )}
    </div>
  );
}
